/**
 * sphereNodeToSphere.ts
 *
 * Adapter that converts SphereNode (from context tree execution) to the legacy
 * Sphere type (from types/global) so that existing components like Node, EditSphere,
 * and AddSphere continue to work during the migration from surface hooks to context hooks.
 */

import type { SphereNode, AssignedPerspective as ContextAssignedPerspective, FlowData } from 'types/context';
import type {
  Sphere,
  SphereProperties,
  AssignedPerspective as GlobalAssignedPerspective,
  AssignedPerspectives,
  ConnectingPerspective,
  SphereStatusPoint as GlobalSphereStatusPoint,
} from 'types/global';
import type { ArcInfo, StageStatus } from 'types/arc';

/**
 * Convert a SphereNode tree into a legacy Sphere tree.
 * Recursively maps children → nearbySpheres and adapts enrichment data.
 */
export function sphereNodeToSphere(node: SphereNode): Sphere {
  const properties: SphereProperties = {
    type: node.schemaType ?? '',
    name: (node.properties.name as string) ?? node.label ?? '',
    ...node.properties,
  } as SphereProperties;

  const sphere: Sphere = {
    id: node.id,
    label: node.label ?? (node.properties.name as string) ?? '',
    glyph: node.glyph,
    glyphColor: node.glyphColor,
    properties,
    nearbySpheres: node.children.map(sphereNodeToSphere),
    edgeCount: node.edgeCount ?? node.childPagination?.totalCount ?? node.children.length,
    hasMoreEdges: node.childPagination?.hasMore ?? false,
  };

  // Map edge metadata to connectingPerspectives
  if (node.edge) {
    const edgeProps = node.edge.properties ?? {};
    const urgencyRaw = edgeProps.urgency;
    const urgencyStringValue = typeof urgencyRaw === 'object' ? '' : urgencyRaw;
    const cp: ConnectingPerspective = {
      id: edgeProps.edgeId as string ?? '',
      name: edgeProps.perspective as string ?? '',
      system_name: edgeProps.perspective as string ?? '',
      urgency: urgencyRaw === undefined ? undefined : String(urgencyStringValue),
      direction: node.edge.direction,
    };
    sphere.connectingPerspectives = [cp];
  }

  // Map assigned perspectives from context format to global format
  if (node.assignedPerspectives && node.assignedPerspectives.length > 0) {
    sphere.assignedPerspectives = mapAssignedPerspectives(node.id, node.assignedPerspectives);
  }

  // Map status — context now returns the full point structure matching global
  if (node.status) {
    sphere.status = node.status as unknown as GlobalSphereStatusPoint;
  }

  // Map deployed approaches for ApproachBadges molecule
  if (node.deployedApproaches && node.deployedApproaches.length > 0) {
    sphere.deployedApproaches = node.deployedApproaches;
  }

  // Map arc data — build sphere.arcs[] for multi-arc support
  const flowDataList = node.flows ?? (node.flow ? [node.flow] : []);
  if (flowDataList.length > 0) {
    // Keep legacy sphere.flow and properties.flow for backward compatibility
    (sphere as any).flow = node.flow?.flowId;
    (sphere as any).stages = node.flow?.stages;
    if (node.flow?.flowProperties) {
      (sphere.properties as any).flow = {
        id: node.flow.flowId,
        label: node.flow.flowLabel ?? '',
        glyph: '',
        properties: {
          type: '',
          name: (node.flow.flowProperties.name as string) ?? '',
          templateSystemName: (node.flow.flowProperties.templateSystemName as string) ?? '',
          arcDefinitionId: (node.flow.flowProperties.arcDefinitionId as string) ?? '',
          ...node.flow.flowProperties,
        },
        perspectives: [],
        stages: node.flow.stages ?? [],
      };
    }

    // Build sphere.arcs from all flow data
    sphere.arcs = flowDataList.map((fd, index) => flowDataToArcInfo(fd, index === 0));
  }

  return sphere;
}

/**
 * Convert context AssignedPerspective[] to global AssignedPerspectives format.
 */
function mapAssignedPerspectives(
  mainSphereId: string,
  contextPerspectives: ContextAssignedPerspective[]
): AssignedPerspectives {
  return contextPerspectives.map((cp): GlobalAssignedPerspective => ({
    mainSphereId,
    perspectiveProperties: {
      id: cp.perspectiveSystemName,
      system_name: cp.perspectiveSystemName,
      name: cp.perspectiveSystemName,
    },
    assigned: cp.assignedSpheres.map((as) => ({
      sphere: {
        id: as.id,
        label: as.label ?? as.properties.name ?? '',
        glyph: as.glyph,
        glyphColor: as.glyphColor,
        properties: {
          type: '',
          name: as.label ?? (as.properties.name as string) ?? '',
          ...as.properties,
        } as SphereProperties,
      } as Sphere,
      edge: {
        edgeId: as.edge?.edgeId ?? '',
        urgency: as.edge?.urgency ?? 0,
      },
    })),
  }));
}

/**
 * Convert FlowData (context enrichment) to ArcInfo (multi-arc model).
 */
function flowDataToArcInfo(fd: FlowData, isFirst: boolean): ArcInfo {
  const props = fd.flowProperties ?? {};
  const isPrimary = props.isPrimary === true || props.isPrimary === 'true' || isFirst;

  const stages: StageStatus[] = (fd.stages ?? []).map((s) => ({
    stageId: s.stageDefinitionId,
    arcId: s.arcId,
    status: (s.status as 'active' | 'not_started' | 'completed') || 'not_started',
    startedAt: s.startedAt || null,
    completedAt: s.completedAt || null,
  }));

  const activeStage = stages.find((s) => s.status === 'active');

  return {
    id: fd.flowId,
    templateSystemName: (props.templateSystemName as string) ?? '',
    templateVersion: 1,
    currentStageId: activeStage?.stageId ?? '',
    stageEnteredAt: activeStage?.startedAt ?? '',
    isPrimary,
    stages,
  };
}

/**
 * Convert a legacy Sphere back to SphereNode (for tree state updates).
 * Used when mutations (add/edit sphere) return Sphere objects that need
 * to be inserted back into the context tree.
 */
export function sphereToSphereNode(sphere: Sphere, level = 0): SphereNode {
  return {
    id: sphere.id,
    label: sphere.label,
    schemaType: sphere.properties?.type,
    glyph: sphere.glyph,
    glyphColor: sphere.glyphColor,
    level,
    properties: sphere.properties as unknown as Record<string, unknown>,
    children: (sphere.nearbySpheres ?? []).map((child) => sphereToSphereNode(child, level + 1)),
    edge: sphere.connectingPerspectives?.[0]
      ? {
          direction: (sphere.connectingPerspectives[0].direction as 'inwards' | 'outwards') ?? 'outwards',
          properties: {},
        }
      : undefined,
    childPagination: sphere.hasMoreEdges
      ? { hasMore: true, totalCount: sphere.edgeCount }
      : undefined,
  };
}
